import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, skip, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { AlgorithmsFacade } from '../page/algorithms.facade';
import { algorithmFacadeProviders } from '../page/algorithms.facade.providers';
import { AlgorithmData } from '../../../state/algorithm.state';

@Component({
    selector: 'ts-algorithm-editor',
    templateUrl: './algorithm-editor.component.html',
    styleUrls: ['./algorithm-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatProgressSpinnerModule,
    ],
    providers: [...algorithmFacadeProviders]
})
export class AlgorithmEditorComponent implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  public form: UntypedFormGroup;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    public facade: AlgorithmsFacade
  ) {
    this.form = new UntypedFormGroup({
      name: new UntypedFormControl('', Validators.required),
      description: new UntypedFormControl(''),
      version: new UntypedFormControl('', Validators.required),
      regulated: new UntypedFormControl(false),
    });
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this._destroy$)).subscribe((params) => {
      const algorithmId = params.get('algorithmId');
      if (algorithmId) {
        this.facade.loadEditorAlgorithm(algorithmId);
      }
    });

    this.facade.editorAlgorithm$
      .pipe(
        filter((a): a is AlgorithmData => !!a),
        takeUntil(this._destroy$)
      )
      .subscribe((algorithm) => {
        this.form.patchValue(
          {
            name: algorithm.name,
            description: algorithm.description ?? '',
            version: algorithm.version,
            regulated: algorithm.regulated,
          },
          { emitEvent: false }
        );
        this.form.markAsPristine();
      });

    this.facade.editorSaveSuccess$.pipe(skip(1), takeUntil(this._destroy$)).subscribe(() => {
      this.snackBar.open('Algorithm updated', undefined, { duration: 2000 });
      this.router.navigate(['/protocols', 'algorithms']);
    });

    this.facade.editorSaveError$
      .pipe(
        filter((e): e is string => !!e),
        takeUntil(this._destroy$)
      )
      .subscribe((msg) => {
        this.snackBar.open(msg, undefined, { duration: 3000 });
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  cancel(): void {
    this.router.navigate(['/protocols', 'algorithms']);
  }

  submit(): void {
    if (this.form.invalid) return;

    const algorithmId = this.route.snapshot.paramMap.get('algorithmId');
    if (!algorithmId) return;

    this.facade.saveEditorMetadata({
      algorithmId,
      name: this.form.get('name')?.value,
      description: this.form.get('description')?.value,
      version: this.form.get('version')?.value,
      regulated: this.form.get('regulated')?.value,
    });
  }
}
